import { inject, injectAll, singleton } from 'tsyringe';
import { DelegateProvider } from '../../interfaces/delegate-provider.interface';
import { Game } from '../../models/game.model';
import { LoggerService } from '../logger.service';
import { RecommendationDatastoreService } from '../datastore-service/recommendation-datastore.service';
import { ConditionContext } from './conditions/interfaces/condition-context.interface';
import {
  ICondition,
  kNameOfCondition,
  MatchResult,
} from './conditions/interfaces/condition.interface';
import { NoRecommendationsReasons } from './no-recommendations-reasons.enums';
import { WeightedRandomAppSelector } from './weighted-random-app-selector';
import { Application } from '../../models/application.model';
import {
  ConditionType,
  getConditionType,
} from './conditions/decorators/condition-type.decorator';
import { NotificationAnalyticsService } from '../notification-analytics/notification-analytics.service';
import { StopWatch } from '../../utils/stop-watch';
import { kDITGameEventsListener } from '../../constants/injectionTokens';
import { IOWGameEventsListener } from '../../interfaces/ow-wrappers/ow-game-events-listener-wrapper.interface';

// -----------------------------------------------------------------------------
export interface IContentRecommendationServiceDelegate {
  onNoRecommendations?(reason: NoRecommendationsReasons): void;
  onShowNotification?(game: Game, application: Application): void;
}

// -----------------------------------------------------------------------------
@singleton()
export class ContentRecommendationService
  implements DelegateProvider<IContentRecommendationServiceDelegate>
{
  private readonly logger = LoggerService.getCategory(
    'ContentRecommendationService',
  );

  private readonly listeners: Set<IContentRecommendationServiceDelegate>;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly weightedRandomAppSelector: WeightedRandomAppSelector,
    @injectAll(kNameOfCondition)
    private readonly conditions: ICondition[],
    private readonly notificationAnalyticsService: NotificationAnalyticsService,
    private readonly stopWatch: StopWatch,
    @inject(kDITGameEventsListener)
    private readonly gameEventListener: IOWGameEventsListener,
  ) {
    this.listeners = new Set<IContentRecommendationServiceDelegate>();
  }

  // ---------------------------------------------------------------------------
  public async gameStarted(gameId: number, originalGameId?: number) {
    this.stopWatch.start();
    this.logger.info(`Game Id ${gameId} started`);
    await this.notificationAnalyticsService.reportNewGameSession(gameId);

    const game = await this.getAndHandleGameConfiguration(gameId);
    if (!game) {
      this.triggerNoRecommendation(
        NoRecommendationsReasons.NoGameConfig,
        gameId,
      );
      this.notificationAnalyticsService.reportNoGameToRecommend(gameId);
      return;
    }

    game.originalGameId = originalGameId;

    if (!game.shouldShowOnStart) {
      this.logger.info('Game configured to wait for game to end ...');
      this.triggerNoRecommendation(
        NoRecommendationsReasons.ConfiguredForEnd,
        gameId,
      );

      await this.triggerGameListener(game);

      return;
    }

    return this.runConditions(game);
  }

  // ---------------------------------------------------------------------------
  // TODO(advoyris): Need to rethink the edge-case where shouldShowOnStart is
  // false but we have a |game.targetGameId|
  public async gameEnded(gameId: number, originalGameId?: number) {
    const game = await this.getAndHandleGameConfiguration(gameId);

    if (!game) {
      this.triggerNoRecommendation(
        NoRecommendationsReasons.NoGameConfig,
        gameId,
      );
      return;
    }

    game.originalGameId = originalGameId;

    if (game.shouldShowOnStart) {
      this.triggerNoRecommendation(
        NoRecommendationsReasons.ConfiguredForStart,
        gameId,
      );

      this.gameEventListener?.stop();
      return;
    }

    const configs = await this.datastoreService.getGlobalConfigurations();
    const timeSinceGameStartedInMin = this.stopWatch.getTime() / 60 / 1000;
    this.stopWatch.stop();

    this.logger.info(
      `Game Id ${gameId} ended, after running for ` +
        `${timeSinceGameStartedInMin} minutes out of the configured ` +
        `${configs.minimumGameSessionTimeToDisplayNotificationMins} minutes `,
    );

    if (
      timeSinceGameStartedInMin <
      configs.minimumGameSessionTimeToDisplayNotificationMins
    ) {
      this.logger.info(
        'Skipping recommendation display because game session was to short',
      );

      this.triggerNoRecommendation(
        NoRecommendationsReasons.MinimumGameSessionLengthNotReached,
        gameId,
      );

      return;
    }

    return this.runConditions(game);
  }

  // ---------------------------------------------------------------------------
  private async getAndHandleGameConfiguration(gameId: number): Promise<Game> {
    try {
      const game = await this.datastoreService.getGame(gameId);

      if (!game) {
        this.logger.info('No game configuration found');
        this.triggerNoRecommendation(
          NoRecommendationsReasons.NoGameConfig,
          gameId,
        );

        return null;
      }

      return game;
    } catch (ex) {
      this.logger.error('error obtaining configurations', ex);
      this.triggerNoRecommendation(
        NoRecommendationsReasons.FailedToQueryServer,
        gameId,
      );

      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // TODO(twolf): Document
  private async runConditions(game: Game): Promise<void> {
    this.logger.info(
      '*** Starting notification display conditions evaluation process ...',
    );

    const [passed, reason] = await this.getGlobalConditionsResult(game);
    if (!passed) {
      return this.triggerNoRecommendation(reason, game.id);
    }

    await this.tryRecommendingApp(game);
  }

  // ---------------------------------------------------------------------------
  private async getGlobalConditionsResult(game: Game): Promise<MatchResult> {
    try {
      const context: ConditionContext = { game };

      const globalConditions = this.conditions.filter(
        (condition) => getConditionType(condition) === ConditionType.Global,
      );

      return await this.passesConditions(context, globalConditions);
    } catch (err) {
      this.logger.error('Failed checking global conditions: ', err);
    }

    return [false, NoRecommendationsReasons.CooldownCrashed];
  }

  // ---------------------------------------------------------------------------
  private async tryRecommendingApp(game: Game) {
    const nonGlobalConditions = this.conditions.filter(
      (cond) => getConditionType(cond) !== ConditionType.Global,
    );

    this.weightedRandomAppSelector.initialize(game, game.apps);

    let selectedApp = this.weightedRandomAppSelector.next();

    // eslint-disable-next-line no-unmodified-loop-condition
    while (selectedApp) {
      const context: ConditionContext = { game, application: selectedApp };

      this.logger.info(`**** Running conditions on: ${selectedApp.name} ...`);
      const [passed, reason] = await this.passesConditions(
        context,
        nonGlobalConditions,
      );

      if (!passed) {
        this.notificationAnalyticsService.reportApplicationConditionResult({
          gameId: game.id,
          reason,
          applicationUid: selectedApp.uid,
        });
      }

      if (passed) {
        return this.triggerShowNotification(game, selectedApp);
      }

      selectedApp = this.weightedRandomAppSelector.next();
    }

    this.notificationAnalyticsService.reportPortfolioExhausted(
      game.id,
      game.originalGameId,
    );
    this.triggerNoRecommendation(
      NoRecommendationsReasons.PortfolioExhausted,
      game.id,
    );
  }

  // ---------------------------------------------------------------------------
  private async passesConditions(
    context: ConditionContext,
    conditions: ICondition[],
  ): Promise<MatchResult> {
    for (const condition of conditions) {
      const shouldSkip = await this.shouldSkipCondition(condition, context);
      if (shouldSkip) {
        this.logger.info(`Skipping condition ${condition.id}`);
        continue;
      }

      const [passed, reason] = await condition.match(context);

      if (!passed) {
        this.logger.info(`${condition.id}: [Failed] (${reason})`);
        return [passed, reason];
      }

      this.logger.info(`${condition.id}: [Passed]`);
    }

    return [true];
  }

  // ---------------------------------------------------------------------------
  private async shouldSkipCondition(
    condition: ICondition,
    context: ConditionContext,
  ) {
    if (!condition.shouldSkip) {
      return false;
    }

    const shouldSkip = await condition.shouldSkip(context);
    return shouldSkip;
  }

  // ---------------------------------------------------------------------------
  private triggerNoRecommendation(
    reason: NoRecommendationsReasons,
    gameId: number,
  ) {
    this.notificationAnalyticsService.reportGlobalConditionResult({
      gameId,
      reason,
    });

    this.logger.info(`No recommendation triggered with reason: ${reason}`);
    for (const listener of this.listeners) {
      if (listener.onNoRecommendations) {
        listener.onNoRecommendations(reason);
      }
    }
  }

  // ---------------------------------------------------------------------------
  private triggerShowNotification(game: Game, application: Application) {
    this.logger.info(
      `Triggering recommendation for game: ${game.id} & application:` +
        ` ${application.name}`,
    );

    for (const listener of this.listeners) {
      if (listener.onShowNotification) {
        listener.onShowNotification(game, application);
      }
    }
  }

  // ---------------------------------------------------------------------------
  private async triggerGameListener(game: Game) {
    const gameTriggerApps = game?.apps?.filter((app) => app?.gameEventTrigger);
    if (!gameTriggerApps) {
      return;
    }

    this.logger.info(
      `Starting game event listener for apps: ${gameTriggerApps
        .map((app) => app.name)
        .join(', ')}`,
    );

    await this.gameEventListener?.start(gameTriggerApps);
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IContentRecommendationServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IContentRecommendationServiceDelegate) {
    this.listeners.delete(listener);
  }
}
